import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '~/utils/cn';

import type { CostEstimate } from '../types/cost-estimate.types';
import { decidedViaLabel } from '../utils/decision-labels';
import { requesterCaption } from '../utils/requester-caption';

import { CostActor } from './cost-actor';
import { RelativeTime } from './relative-time';
import { SubsidiaryName } from './subsidiary-name';

/** Decided statuses that carry a decider (CANCELLED's `system` is shown as the run ending). */
const DECIDED = new Set(['APPROVED', 'REJECTED', 'EXPIRED', 'CANCELLED']);

/**
 * Who started the run: the requester (name, email on hover) or, for a schedule-fired run,
 * "Scheduled by …"; "Schedule" / "System" when no person was found. Em-dash on estimates that
 * predate the audit fields.
 */
export function CostRequester({
  estimate,
  showAvatar = false,
  className,
  testId = 'cost-requester',
}: {
  estimate: CostEstimate;
  showAvatar?: boolean;
  className?: string;
  /** `data-testid` on the requester label (default `cost-requester`). */
  testId?: string;
}) {
  const { t } = useTranslation('common');
  if (estimate.requestedBy) {
    return (
      <CostActor
        value={estimate.requestedBy}
        name={estimate.requestedByName}
        showAvatar={showAvatar}
        testId={testId}
        {...(className ? { className } : {})}
      />
    );
  }
  const fallback =
    estimate.triggerType === 'SCHEDULED'
      ? t('costEstimate.audit.schedule')
      : estimate.triggerType === 'SYSTEM'
        ? t('costEstimate.audit.system')
        : '—';
  return (
    <span data-testid={testId} className={cn('text-muted-foreground', className)}>
      {fallback}
    </span>
  );
}

/**
 * Labelled audit grid for an estimate — requester, requested time, subsidiary and, once
 * decided, who decided via which channel and when, plus any decision notes. Shared by the run
 * card, the details page and the Pending list so the three read the same.
 */
export function CostAuditMeta({
  estimate,
  showSubsidiary = true,
  showDecision = true,
  className,
  testId = 'cost-audit-meta',
}: {
  estimate: CostEstimate;
  /** `data-testid` root; fields are `-{requester|requested|subsidiary|decided-by|notes}` (+ `-value`). */
  testId?: string;
  showSubsidiary?: boolean;
  /** Off where a separate decision panel already shows the decider and notes. */
  showDecision?: boolean;
  className?: string;
}) {
  const { t } = useTranslation('common');
  const decided =
    showDecision && DECIDED.has(estimate.status) && (!!estimate.decidedBy || !!estimate.decidedAt);
  const via = decidedViaLabel(t, estimate.decidedVia);
  return (
    <dl
      data-testid={testId}
      className={cn('grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4', className)}
    >
      <MetaItem label={requesterCaption(estimate, t)} testId={`${testId}-requester`}>
        <CostRequester estimate={estimate} showAvatar testId={`${testId}-requester-value`} />
      </MetaItem>
      <MetaItem label={t('costEstimate.audit.requested')} testId={`${testId}-requested`}>
        <RelativeTime iso={estimate.createdAt} testId={`${testId}-requested-value`} />
      </MetaItem>
      {showSubsidiary ? (
        <MetaItem label={t('costApprovals.subsidiary')} testId={`${testId}-subsidiary`}>
          <SubsidiaryName
            id={estimate.subsidiaryId}
            name={estimate.subsidiaryName}
            testId={`${testId}-subsidiary-value`}
          />
        </MetaItem>
      ) : null}
      {decided ? (
        <MetaItem label={t('costEstimateDetail.decidedBy')} testId={`${testId}-decided-by`}>
          <span className="flex min-w-0 flex-col gap-0.5">
            <CostActor
              value={estimate.decidedBy}
              name={estimate.decidedByName}
              showAvatar
              testId={`${testId}-decided-by-value`}
            />
            <span className="text-muted-foreground text-xs" data-testid={`${testId}-decided-via`}>
              {via ? `${via} · ` : null}
              {estimate.decidedAt ? (
                <RelativeTime iso={estimate.decidedAt} testId={`${testId}-decided-at`} />
              ) : null}
            </span>
          </span>
        </MetaItem>
      ) : null}
      {showDecision && estimate.decisionNotes ? (
        <div className="col-span-full flex flex-col gap-1" data-testid={`${testId}-notes`}>
          <dt className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
            {t('costEstimate.audit.notes')}
          </dt>
          <dd
            data-testid={`${testId}-notes-value`}
            className="text-foreground border-border border-l-2 pl-3 text-sm break-words whitespace-pre-line"
          >
            {estimate.decisionNotes}
          </dd>
        </div>
      ) : null}
    </dl>
  );
}

/** Caption over a value, as a `<dt>` / `<dd>` pair. */
function MetaItem({
  label,
  children,
  testId,
}: {
  label: string;
  children: ReactNode;
  testId: string;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1" data-testid={testId}>
      <dt className="text-muted-foreground text-xs font-medium tracking-wide uppercase">{label}</dt>
      <dd className="text-foreground min-w-0 text-sm">{children}</dd>
    </div>
  );
}
